import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import configData from '../configure'; // Import the configData module

// const BASE_URL = 'https://org1.egurukulapps.com/campusidentity';

// Fetch all students for a class with initial quick fetch of 50
export const fetchAllStudents = createAsyncThunk(
  'students/fetchAllStudents',
  async ({ classId, axios }, { rejectWithValue, dispatch }) => {
    try {
      let allStudents = [];
      let lastEvaluatedKey = null;
      const initialPageSize = 50; // Quick fetch for first 50 students
      const subsequentPageSize = 500; // Larger page size for background fetches
      let totalCount = 0; // Track API-provided total count if available

      // Initial fetch for the first 50 students
      const initialParams = { pageSize: initialPageSize };
      const initialResponse = await axios.get(`${configData.GET_STUDENTS_BY_CLASS}/${classId}`, {
        params: initialParams,
      });
      const initialData = initialResponse.data;
      console.log('Initial Response:', {
        classId,
        itemsLength: initialData.items?.length,
        totalCount: initialData.totalCount,
        firstItem: initialData.items?.[0] // Log the first item to check structure
      });

      if (!initialData.items || !Array.isArray(initialData.items)) {
        throw new Error('Unexpected response format: items missing or not an array');
      }

      allStudents = [...initialData.items];
      // Set lastEvaluatedKey to the userId of the last student (if any)
      lastEvaluatedKey =
        initialData.items.length > 0
          ? initialData.items[initialData.items.length - 1].user?.userId
          : null;
      totalCount = initialData.totalCount || allStudents.length;
      console.log('Initial Fetch:', {
        items: initialData.items.length,
        lastEvaluatedKey,
        totalCount,
      });

      // Dispatch the initial batch immediately
      dispatch(
        studentSlice.actions.updateStudents({
          classId,
          students: allStudents,
          totalCount: totalCount,
        })
      );
      console.log('Initial Dispatch:', { totalStudents: allStudents.length, totalCount });

      // Background fetch for remaining students
      while (lastEvaluatedKey) {
        const params = { pageSize: subsequentPageSize, lastEvaluatedKey };
        try {
          const response = await axios.get(`${configData.GET_STUDENTS_BY_CLASS}/${classId}`, { params });
          const data = response.data;
          console.log('Background Response:', {
            classId,
            itemsLength: data.items?.length,
            totalCount: data.totalCount,
          });

          if (!data.items || !Array.isArray(data.items)) {
            throw new Error('Unexpected response format: items missing or not an array');
          }

          if (data.items.length === 0) {
            console.warn('Empty items received, stopping fetch');
            lastEvaluatedKey = null;
            break;
          }

          allStudents = [...allStudents, ...data.items];
          // Update lastEvaluatedKey to the userId of the last student
          lastEvaluatedKey =
            data.items.length > 0
              ? data.items[data.items.length - 1].user?.userId
              : null;
          totalCount = data.totalCount || allStudents.length;
          console.log('Background Fetch:', {
            items: data.items.length,
            totalStudents: allStudents.length,
            lastEvaluatedKey,
            totalCount,
          });

          // Dispatch incremental updates
          dispatch(
            studentSlice.actions.updateStudents({
              classId,
              students: allStudents,
              totalCount: totalCount,
            })
          );
        } catch (error) {
          console.error('Background fetch error:', error.response?.data || error.message);
          return rejectWithValue(
            error.response?.data || 'Failed to fetch remaining students: ' + error.message
          );
        }
      }

      console.log('Fetch completed:', { classId, totalStudents: allStudents.length, totalCount });

      if (totalCount && allStudents.length < totalCount) {
        console.warn(`Incomplete fetch: Expected ${totalCount}, got ${allStudents.length}`);
      }

      return { students: allStudents, totalCount: totalCount || allStudents.length, classId };
    } catch (error) {
      console.error('Initial fetch error:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Fetch a single student by userId
export const fetchStudentById = createAsyncThunk(
  'students/fetchStudentById',
  async ({ userId, axios }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${configData.GET_STUDENT_BY_ID}/${userId}`);
      return response.data; // Expecting a single student object
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const studentSlice = createSlice({
  name: 'students',
  initialState: {
    allStudents: {}, // { classId: [students] }
    filteredStudents: [], // For search results
    currentClassId: null,
    searchQuery: '',
    loading: false,
    error: null,
    pagination: {
      currentPage: 0,
      pageSize: 50,
      totalItems: 0,
      totalPages: 0,
    },
    totalCountsByClass: {},
    singleStudent: null, // New state for single student data
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentPage: (state, action) => {
      state.pagination.currentPage = action.payload;
    },
    setSearchQuery: (state, action) => {
      const { searchQuery, classId } = action.payload;
      state.searchQuery = searchQuery;
      const students = state.allStudents[classId] || [];

      if (searchQuery.trim()) {
        const searchValue = searchQuery.toLowerCase().trim();
        state.filteredStudents = students.filter((user) => {
          const firstName = user.user?.firstName?.toLowerCase() || '';
          const lastName = user.user?.lastName?.toLowerCase() || '';
          const gender = user.user?.gender?.toLowerCase() || '';
          const city = user.user?.addressDetails?.[0]?.city?.toLowerCase() || '';
          const admissionId = user.studentInfo?.admissionId?.toLowerCase() || '';
          const email = user.user?.email?.toLowerCase() || '';

          return (
            firstName.includes(searchValue) ||
            lastName.includes(searchValue) ||
            gender.includes(searchValue) ||
            city.includes(searchValue) ||
            admissionId.includes(searchValue) ||
            email.includes(searchValue)
          );
        });
      } else {
        state.filteredStudents = students;
      }

      state.pagination.totalItems = state.filteredStudents.length;
      state.pagination.totalPages =
        Math.ceil(state.filteredStudents.length / state.pagination.pageSize) || 1;
      state.pagination.currentPage = 0; // Reset to first page on search
    },
    clearCache: (state, action) => {
      const { classId } = action.payload;
      delete state.allStudents[classId];
      delete state.totalCountsByClass[classId];
      if (state.currentClassId === classId) {
        state.filteredStudents = [];
        state.searchQuery = '';
        state.pagination.totalItems = 0;
        state.pagination.totalPages = 0;
        state.pagination.currentPage = 0;
        state.currentClassId = null;
      }
    },
    resetClassState: (state, action) => {
      const { classId } = action.payload;
      state.currentClassId = classId;
      state.filteredStudents = [];
      state.searchQuery = '';
      state.pagination.currentPage = 0;
      state.pagination.totalItems = 0;
      state.pagination.totalPages = 0;
    },
    updateStudents: (state, action) => {
      const { classId, students, totalCount } = action.payload;
      state.allStudents[classId] = students;
      state.totalCountsByClass[classId] = totalCount;
      state.currentClassId = classId;

      // Update filtered students if no search query
      if (!state.searchQuery) {
        state.filteredStudents = students;
        state.pagination.totalItems = totalCount;
        state.pagination.totalPages = Math.ceil(totalCount / state.pagination.pageSize) || 1;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllStudents.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        // Clear data for the new class to avoid showing stale data
        const classId = action.meta.arg.classId;
        if (state.currentClassId !== classId) {
          state.currentClassId = classId;
          state.filteredStudents = [];
          state.pagination.totalItems = 0;
          state.pagination.totalPages = 0;
          state.pagination.currentPage = 0;
        }
      })
      .addCase(fetchAllStudents.fulfilled, (state, action) => {
        state.loading = false;
        const { students, totalCount, classId } = action.payload;
        state.allStudents[classId] = students;
        state.totalCountsByClass[classId] = totalCount;
        state.currentClassId = classId;

        // Update filtered students if no search query
        if (!state.searchQuery) {
          state.filteredStudents = students;
          state.pagination.totalItems = totalCount;
          state.pagination.totalPages = Math.ceil(totalCount / state.pagination.pageSize) || 1;
        }
      })
      .addCase(fetchAllStudents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch students';
        state.filteredStudents = [];
        state.pagination.totalItems = 0;
        state.pagination.totalPages = 0;
      })
      .addCase(fetchStudentById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStudentById.fulfilled, (state, action) => {
        state.loading = false;
        state.singleStudent = action.payload;
        state.filteredStudents = [action.payload]; // Populate filteredStudents for table
      })
      .addCase(fetchStudentById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch student';
      });
  },
});

export const {
  clearError,
  setCurrentPage,
  setSearchQuery,
  clearCache,
  resetClassState,
  updateStudents,
} = studentSlice.actions;
export default studentSlice.reducer;